import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { cartApi } from '../api/cart';
import type {
  AddToCartDto,
  ApplyDiscountCodeDto,
  ApplyGiftCardDto,
  CartDto,
  SetCartNoteDto,
  SetCartShippingDto,
  UpdateCartItemDto,
} from '../api/types';
import { useToast } from '../components/Toast';
import { trackAddToCart } from '../lib/analytics';

export const cartQueryKey = ['cart'] as const;

interface CartState {
  cart: CartDto | null;
  isLoading: boolean;
  itemCount: number;
  /** Sepeti degistiren bir istek suruyor mu (butonlari kilitlemek icin). */
  busy: boolean;
  addItem: (dto: AddToCartDto) => Promise<void>;
  updateItem: (cartItemId: number, dto: UpdateCartItemDto) => Promise<void>;
  removeItem: (cartItemId: number) => Promise<void>;
  clear: () => Promise<void>;
  applyDiscountCode: (dto: ApplyDiscountCodeDto) => Promise<void>;
  removeDiscountCode: (code: string) => Promise<void>;
  applyGiftCard: (dto: ApplyGiftCardDto) => Promise<void>;
  removeGiftCard: (giftCardId: number) => Promise<void>;
  setShipping: (dto: SetCartShippingDto) => Promise<void>;
  setNote: (dto: SetCartNoteDto) => Promise<void>;
  reload: () => void;
}

const CartContext = createContext<CartState | null>(null);

export function useCart(): CartState {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart yalnızca CartProvider içinde kullanılabilir.');
  }
  return context;
}

export function CartProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const query = useQuery({
    queryKey: cartQueryKey,
    queryFn: () => cartApi.get(),
    staleTime: 15_000,
  });

  /** Her sepet islemi guncel sepeti doner; onbellegi dogrudan tazeleriz. */
  const write = useCallback(
    (next: CartDto) => queryClient.setQueryData(cartQueryKey, next),
    [queryClient],
  );

  const mutation = useMutation({
    mutationFn: (action: () => Promise<CartDto>) => action(),
    onSuccess: write,
    onError: (error) => toast.fromError(error, 'Sepet güncellenemedi.'),
  });

  const run = useCallback(
    async (action: () => Promise<CartDto>) => {
      await mutation.mutateAsync(action);
    },
    [mutation],
  );

  const value = useMemo<CartState>(() => {
    const cart = query.data ?? null;

    return {
      cart,
      isLoading: query.isLoading,
      itemCount: cart?.itemCount ?? 0,
      busy: mutation.isPending,
      addItem: (dto) =>
        run(async () => {
          const res = await cartApi.addItem(dto);
          const addedItem =
            res.items.find((i) => i.productVariantId === dto.productVariantId) ||
            res.items[res.items.length - 1];
          if (addedItem) {
            trackAddToCart({
              id: addedItem.productId,
              name: addedItem.productTitle,
              price: addedItem.unitPrice,
              quantity: dto.quantity || 1,
              variant: addedItem.variantTitle || undefined,
            });
          }
          return res;
        }),
      updateItem: (cartItemId, dto) => run(() => cartApi.updateItem(cartItemId, dto)),
      removeItem: (cartItemId) => run(() => cartApi.removeItem(cartItemId)),
      clear: () => run(() => cartApi.clear()),
      applyDiscountCode: (dto) => run(() => cartApi.applyDiscountCode(dto)),
      removeDiscountCode: (code) => run(() => cartApi.removeDiscountCode(code)),
      applyGiftCard: (dto) => run(() => cartApi.applyGiftCard(dto)),
      removeGiftCard: (giftCardId) => run(() => cartApi.removeGiftCard(giftCardId)),
      setShipping: (dto) => run(() => cartApi.setShipping(dto)),
      setNote: (dto) => run(() => cartApi.setNote(dto)),
      reload: () => {
        void queryClient.invalidateQueries({ queryKey: cartQueryKey });
      },
    };
  }, [query.data, query.isLoading, mutation.isPending, run, queryClient]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}
